import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faHandshake,
  faUsers,
  faGavel,
  faChartLine,
  faCircleNodes,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

/* ────────────────────────────────────────────────
   Dummy Data – enriched from your research draft
───────────────────────────────────────────────── */

const CORE_LAYERS = [
  {
    name: "Individual",
    subtitle: "Self & Clarity",
    color: "from-blue-900 to-blue-700",
    icon: faUser,
    desc: "Structured empathy • Root-cause clarification using 5Whys • Normalize doubt & fear • Encourage self-agency • Know when to refer to professionals • Pre-therapy clarity layer",
  },
  {
    name: "Relational",
    subtitle: "People & Conflict",
    color: "from-orange-900 to-orange-700",
    icon: faHandshake,
    desc: "Facilitate non-blaming dialogue • De-personalize issues • Surface misaligned systems & incentives • Restore dignity for all parties • Prevent personal problems from escalating to community level",
  },
  {
    name: "Community",
    subtitle: "Groups & Patterns",
    color: "from-teal-900 to-teal-700",
    icon: faUsers,
    desc: "Aggregate anonymized recurring issues • Identify festival / seasonal stress hotspots • Surface inclusion & equity gaps • Provide early-warning signals to leadership • Pattern recognition over anecdotes",
  },
  {
    name: "Governance",
    subtitle: "Leadership & Ethics",
    color: "from-indigo-900 to-indigo-700",
    icon: faGavel,
    desc: "Transparent escalation paths • Role & boundary clarity • Ethical pattern-based decisions • Volunteer & leader burnout detection • Succession planning • Shift from 'who is at fault' to 'what system allowed this'",
  },
];

const WORKFLOW_STEPS = [
  { num: 1, title: "Observe & Listen", desc: "Structured empathy & initial root-cause prompts" },
  { num: 2, title: "Facilitate Dialogue", desc: "De-personalize conflict • Surface systemic causes" },
  { num: 3, title: "Aggregate Patterns", desc: "Anonymized insight collection • AI-assisted pattern detection" },
  { num: 4, title: "Ethical & Confidential Reporting", desc: "Feed non-judgmental insights upward • Maintain privacy & boundaries" },
];

const INSTITUTIONS = [
  { name: "Temples", icon: "🛕", volunteer: "Observe devotee distress & festival stress", governance: "Resource allocation & committee dashboards" },
  { name: "Mosques", icon: "🕌", volunteer: "Track charity/zakat patterns & crowd issues", governance: "Early-warning for bottlenecks & scheduling" },
  { name: "Gurdwaras", icon: "🪯", volunteer: "Monitor langar/seva stress & volunteer load", governance: "Balanced shifts & inclusion reports" },
  { name: "Churches", icon: "⛪", volunteer: "Capture youth/family concerns & patterns", governance: "Parish council program adjustments" },
];

const KPIS = [
  { label: "Active Volunteers", target: ">80% trained & reporting monthly" },
  { label: "Individual Issues Addressed", target: "70–80% within tiered escalation" },
  { label: "Repeat Community Conflicts", target: "≥50% reduction in 6 months" },
  { label: "Leader / Volunteer Burnout Rate", target: "<10% high-stress reports" },
  { label: "Insights Feeding Governance Decisions", target: "100% of aggregated patterns reviewed" },
];

const TOC_ITEMS = [
  "Executive Summary",
  "Problem Statement",
  "CoreSense™ Framework",
  "Volunteer Workflow",
  "AI Integration & Safeguards",
  "HR / Psychology / Governance Mapping",
  "Indian Institutional Adaptation",
  "Adoption Metrics & KPIs",
  "Pilot Roadmap",
  "Training Modules",
];

/* ────────────────────────────────────────────────
   Component
───────────────────────────────────────────────── */

const CoreSenseHone = () => {
  const { isLoggedIn = false, user = {} } = useSelector((state) => state.authReducer || {});
  const userName = user?.name || "Volunteer";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-950 text-gray-100 font-sans">

      {/* ── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-blue-600/40 px-4 py-3.5 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-xl lg:text-2xl font-bold text-yellow-200 tracking-tight">
            🕊️ Think5Whys CoreSense™
          </div>
          <div className="text-xs lg:text-sm text-gray-100 mt-0.5">
            Clarity for People, Communities, and Purpose
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-teal-300 text-xs font-medium">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-10 px-4 py-1.5 bg-red-700/90 hover:bg-red-600 rounded-md text-sm transition-colors"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-10 px-4 py-1.5 border border-blue-500/60 hover:border-blue-400 rounded-md text-sm transition-colors"
            >
              🔐 
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden w-10 text-xl text-blue-300 focus:outline-none"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* ── Mobile menu ────────────────────────────────── */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-5">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <span className="text-teal-300 font-medium text-xs">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-10 py-2.5 bg-red-700/90 hover:bg-red-600 rounded-md transition-colors"
              >
                ⏻
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-10 py-2.5 border border-blue-500/60 hover:border-blue-400 rounded-md transition-colors"
            >
              🔐
            </button>
          )}
        </div>
      )}

      {/* ── Main content ───────────────────────────────── */}
      <main className="flex-1 px-4 py-6 w-full space-y-2">

        {/* Table of Contents teaser – mobile collapsible feel */}
        <section className="bg-gray-900/60 rounded-xl p-2 border border-gray-700/50 relative">
            <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg lg:text-xl font-bold text-blue-300">
                About CoreSense
                </h2>
                
                {/* Info / ? button */}
                <button
                onClick={() => setShowGuide(!showGuide)}
                className="w-8 h-8 rounded-full bg-blue-800/60 hover:bg-blue-700/80 flex items-center justify-center text-blue-200 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Show CoreSense overview"
                title="Learn more about the structure"
                >
                ?
                </button>
            </div>

            {/* Collapsible / toggleable detailed overview */}
            {showGuide && (
                <div className="mt-2 pt-2 border-t border-gray-700/50 animate-fade-in">
                    {/* <h3 className="text-lg font-semibold text-teal-300 mb-2">
                        CoreSense Guide & Framework Overview
                    </h3>
                     */}
                    <div className="space-y-1 text-sm lg:text-base text-gray-300 leading-relaxed">
                        <div>
                            <strong className="text-blue-300 block mb-1.5">1. Executive Summary</strong>
                            <p className="text-white">Global and Indian context: religious institutions serve millions but face burnout, conflict, governance gaps. CoreSense offers a volunteer-led, AI-assisted human systems approach — neutral, ethical, culturally sensitive.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">2. The Challenge Today</strong>
                            <p className="text-white">Burnout of leaders & volunteers • Unstructured emotional & conflict support • Fragmented feedback • Reactive rather than preventive community care • No early-warning systems.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">3. CoreSense Framework – Four Layers</strong>
                            <p className="text-white">Individual Sense → Relational Sense → Community Sense → Governance Sense<br/>
                            A layered model for moving from personal clarity to ethical, transparent institutional decisions.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">4. Volunteer Workflow</strong>
                            <p className="text-white">Observe & Listen → Facilitate Dialogue → Aggregate Patterns → Ethical & Confidential Reporting → Leadership Insights. Human-in-the-loop with optional AI assistance.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">5. AI as Supportive Co-Guide</strong>
                            <p className="text-white">Assistive only (never authoritative) • Pattern detection • Root-cause prompting • Ethical boundary reminders • Privacy-first & explainable design.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">6. Alignment with Modern Practices</strong>
                            <p className="text-white">Maps to HR (wellbeing & conflict management), Psychology (root-cause clarity, humanistic approach), Community Governance (transparency, resilience, early signals).</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">7. Indian Institutional Fit</strong>
                            <p className="text-white">Temples, Mosques, Gurdwaras, Churches, Monastic centers — volunteer-led entry, festival-aware, hierarchy-respecting, language-localized adaptations.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">8. Adoption & Success Indicators</strong>
                            <p className="text-white">Volunteer engagement rate • Issues addressed • Conflict recurrence drop • Burnout reduction • Pattern insights used in governance meetings.</p>
                        </div>

                        <div>
                            <strong className="text-blue-300 block mb-1.5">9. Training & Pilot Pathway</strong>
                            <p className="text-white">Volunteer orientation • Layer-by-layer guidance • Scenario practice • Monthly peer reviews • Start with small pilots → refine → scale across institutions.</p>
                        </div>

                        <div className="text-teal-400/80 italic mt-4 pt-3 border-t border-gray-700/40 text-center">
                            A neutral support layer — never replaces faith, rituals or moral authority — only strengthens human connection, clarity and resilience.
                        </div>
                    </div>
                </div>
            )}
            </section>

        {/* Core Framework Layers */}
        <section>
          {/* <h2 className="text-xl lg:text-2xl font-bold mb-2 text-center lg:text-left text-blue-300">
            CoreSense™ Framework
          </h2> */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {CORE_LAYERS.map((layer) => (
              <div
                key={layer.name}
                className={`rounded-xl bg-gradient-to-br ${layer.color} p-5 lg:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-white/5`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <FontAwesomeIcon icon={layer.icon} className="text-4xl text-white/90" />
                  <div>
                    <h3 className="text-lg lg:text-xl font-semibold text-yellow-200">{layer.name}</h3>
                    <p className="text-xs lg:text-sm text-gray-300 mt-0.5">{layer.subtitle}</p>
                  </div>
                </div>
                <p className="text-sm lg:text-base text-gray-200 leading-relaxed">{layer.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Volunteer Workflow – more visual */}
        <section className="bg-gray-900/60 rounded-xl p-5 lg:p-7 border border-teal-600/30">
          {/* <h2 className="text-xl lg:text-2xl font-bold mb-2 text-teal-300 text-center">
            Volunteer Workflow
          </h2> */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 relative">
            {WORKFLOW_STEPS.map((step) => (
              <div
                key={step.num}
                className="bg-gray-800/80 rounded-lg p-5 text-center border border-teal-500/40 hover:border-teal-400 transition-all group"
              >
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-teal-900/60 flex items-center justify-center text-xl font-bold text-teal-300 group-hover:bg-teal-800 transition-colors">
                  {step.num}
                </div>
                <h4 className="font-semibold mb-2 text-lg text-white">{step.title}</h4>
                <p className="text-sm text-gray-100">{step.desc}</p>
              </div>
            ))}
            {/* Decorative connecting arrows (simple) */}
            <div className="hidden lg:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-teal-600/40 -translate-y-1/2 z-0" />
          </div>
          <p className="mt-8 text-center text-gray-100 text-sm lg:text-base italic">
            AI assists reflection • Human always in control • Privacy-first & bias-aware design
          </p>
        </section>

        {/* Indian Institutions */}
        <section>
          {/* <h2 className="text-xl lg:text-2xl font-bold mb-2 text-center lg:text-left text-orange-300">
            Adaptation for Indian Institutions
          </h2> */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {INSTITUTIONS.map((inst) => (
              <div
                key={inst.name}
                className="bg-gray-800/80 rounded-xl p-5 lg:p-6 border border-orange-600/30 hover:border-orange-500 transition-all shadow-md hover:shadow-lg"
              >
                <div className="text-3xl lg:text-6xl mb-4 text-center text-white">{inst.icon}</div>
                <h3 className="text-lg lg:text-xl mb-2 text-center text-white">{inst.name}</h3>
                <div className="space-y-2 text-sm lg:text-lg text-gray-300">
                  <p className="text-white text-xs lg:text-sm"><strong className="text-orange-400">Volunteer Role:</strong> {inst.volunteer}</p>
                  <p className="text-white text-xs lg:text-sm"><strong className="text-orange-400">Governance Use:</strong> {inst.governance}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* KPIs */}
        <section>
          {/* <h2 className="text-xl lg:text-2xl font-bold mb-2 text-center lg:text-left text-indigo-300">
            Adoption & Impact
          </h2> */}
          <div className="overflow-x-auto rounded-md border border-gray-700/60 bg-gray-900/40">
            <table className="min-w-full">
              <thead className="bg-gray-800/80">
                <tr>
                  <th className="px-1 py-1 text-left text-blue-300 font-semibold text-xs lg:text-sm">Adoption</th>
                  <th className="px-1 py-1 text-left text-blue-300 font-semibold text-xs lg:text-sm">Impact</th>
                </tr>
              </thead>
              <tbody>
                {KPIS.map((kpi, i) => (
                  <tr key={i} className="border-t border-gray-700/50 hover:bg-gray-800/60 transition-colors">
                    <td className="px-1 py-1 text-white text-xs lg:text-sm">{kpi.label}</td>
                    <td className="px-1 py-1 font-medium text-teal-300 text-xs lg:text-sm">{kpi.target}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Small roadmap teaser */}
        <section className="bg-gradient-to-r from-indigo-950 to-blue-950 rounded-xl p-6 text-center border border-indigo-500/30">
          <h2 className="text-xl lg:text-2xl font-bold mb-2 text-indigo-200">Adoption Roadmap</h2>
          <div className="grid grid-cols-3 lg:grid-cols-3 gap-1 place-items-center">
            <div className="bg-orange-700/80 text-white px-2 py-3.5 rounded-lg font-semibold">Pilot Launch</div>
            <FontAwesomeIcon icon={faArrowRight} className="text-3xl text-gray-400 hidden sm:block" />
            <div className="bg-teal-700/80 text-white px-2 py-3.5 rounded-lg font-semibold text-sm lg:text-lg">Feedback & Optimize</div>
            <FontAwesomeIcon icon={faArrowRight} className="text-3xl text-gray-400 hidden sm:block" />
            <div className="bg-indigo-700/80 text-white px-2 py-3.5 rounded-lg font-semibold text-sm lg:text-lg">Scale & Expand</div>
          </div>
          <p className="mt-2 text-gray-300 text-xs lg:text-sm">Volunteer-first • Ethical AI-assisted • Institution-respecting growth</p>
        </section>

      </main>

      {/* ── Footer ─────────────────────────────────────── */}
      <footer className="bg-gray-900 border-t border-blue-600/30 py-6 px-4 text-center lg:text-left">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <div className="text-yellow-200/90">
            © {new Date().getFullYear()} Think5Whys Global DeepTech Solutions
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/about-us" className="hover:text-green-300 transition-colors">About</Link>
            <Link to="/privacy-policy" className="hover:text-green-300 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-green-300 transition-colors">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-green-300 transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CoreSenseHone;